import { appendDpopPublicKey } from '../dpop/appendDpopPublicKey';
import { withDpopLoginBinding } from '../dpop/loginBinding';
import { executeFetch } from '../utils/fetch';
import type { QueryMeta } from '../utils/fetch/types';
import getDomain from '../utils/url/getDomain';
import { AUTH_LOGIN_URL } from './const';

export interface AuthLoginRequest {
  formData: unknown;
  identityProviderId: string;
  returnTo?: string;
}

export interface AuthLoginResponse {
  redirectUrl: string;
  sessionId?: string;
}

/**
 * The ONE way to call `POST /auth/login`.
 *
 * Login is where a session gets bound to this browser's DPoP key, and it is the only
 * moment that binding can happen: the key is offered in the login body, and the gateway
 * pins the session to it. A login posted without the binding succeeds — it returns a
 * redirectUrl and the user lands in the app — and then every DPoP-gated request on that
 * session 401s, with nothing at the login step to suggest why.
 *
 * That is why this lives here and takes no "skip the binding" escape hatch. Callers had
 * hand-rolled their own `/auth/login` fetch (carbon's `useAuthLogin` and the app-builder
 * SDK's), and the SDK's copy silently omitted all three moving parts — the key pair, the
 * public key in the body, and the proof on the request. Reach for this rather than
 * writing the fetch again.
 *
 * `meta` carries the caller's routing (apiDomain + the `x-ua-app` interface header). A
 * mutation's `mutationFn` gets no react-query meta from context the way a queryFn does,
 * so the SDK reads it off the QueryClient's defaults and passes it in; carbon's platform
 * callers are already on the right host and pass nothing.
 */
export function authLogin(request: AuthLoginRequest, options?: { meta?: Record<string, unknown> }) {
  return withDpopLoginBinding(
    (dpopKeyPair) =>
      executeFetch<AuthLoginResponse>(
        {
          url: AUTH_LOGIN_URL,
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          data: appendDpopPublicKey(request, dpopKeyPair?.publicKey),
        },
        // the request must prove it holds the key it is offering, and that key is not in
        // the store yet — withDpopLoginBinding saves it only once the login resolves
        { meta: options?.meta, dpopKeyPair },
      ),
    // keys are stored per backend, so the pair has to be filed under the origin this login
    // targets — the SDK passes an apiDomain, and filing under the page's own origin instead
    // would leave every later request looking up a key that was never saved there
    getDomain(options?.meta as QueryMeta | undefined),
  );
}
